import {
  deleteProgress,
  getUserProgresses,
  postUserProgress,
} from "../Logic/ModuleApi";
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import initializeSteps, { iterateModuleAndReturnSteps } from "../Logic/Steps";
import { getModule } from "../Logic/ModuleApi";
import { Module, ModuleState } from "types/type.module";
import { Style, UserProgress } from "types/types";
import {
  addTerm,
  emptyModule,
  iterateModule,
  loadStyles,
  removeTerm,
  setCurrentChapter,
  activateManualForQuestion,
  deactivateManualForQuestion,
  setHalfPage,
  setActiveIndex,
} from "feature/app/actions";
import { leanEntries } from "Constants/defaultStyles";
import { RootState } from "./store";
import { get, getWithHeaders, post } from "utils/api";

type AuthType = "auth0" | "ms" | "nobocap" | "";
type NobocapDetails = { firstName: string; lastName: string; email: string; };

export interface AppState {
  style: Style;
  module: Module | null;
  tableOfContent: boolean;
  isHalfPage: boolean;
  refs: boolean;
  terms: Array<{ id: string; title?: string }>;
  error: boolean;
  loading: boolean;
  toBePrinted: any[];
  userProgresses?: UserProgress[];
  toResetCallback: any | null;
  savingProgress: string;
  questionsWithManualActive?: string[];
  moduleState: ModuleState | null;
  authType: AuthType;
  nobocapDetails: NobocapDetails;
}

const initialState: AppState = {
  style: leanEntries,
  module: null,
  tableOfContent: false,
  isHalfPage: false,
  refs: false,
  terms: [],
  error: false,
  loading: false,
  toBePrinted: [],
  toResetCallback: null,
  savingProgress: "",
  questionsWithManualActive: [],
  moduleState: null,
  authType: "",
  nobocapDetails: {} as NobocapDetails,
};

/**
   create store slice for app,
 */
export const appSlice = createSlice({
  name: "app",
  initialState: initialState,
  reducers: {
    setTableOfContent: (state) => {
      state.tableOfContent = !state.tableOfContent;
    },
    setRefs: (state) => {
      state.refs = !state.refs;
    },
    setLoading: (state) => {
      state.loading = true;
    },
    setLoadingFalse: (state) => {
      state.loading = false;
    },

    setProgress: (state, { payload }: PayloadAction<string>) => {
      if (state.moduleState) {
        const newState: ModuleState["initialization"] & { toBePrinted: any[] } =
          JSON.parse(payload);
        state.moduleState.initialization = newState;
        const activeIndex = newState.selectedSteps.findIndex(
          ({ iterated, answers }) => !iterated && !answers
        );
        state.moduleState.activeIndex = activeIndex !== -1 ? activeIndex : 0;
        state.toBePrinted = newState.toBePrinted || [];
      }
    },
    setReset: (state, action) => {
      state.toResetCallback = action.payload;
    },
    setSavingProgress: (state) => {
      state.savingProgress = "";
    },
    setAuthType: (state, { payload }: PayloadAction<AuthType>) => {
      state.authType = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadStyles, (state, { payload }) => {
        if (!payload) {
          state.style = { ...leanEntries };
        }

        state.style = {
          ...leanEntries,
          ...(payload?.color1 ? { color1: payload.color1 } : { color1: "#262F3D" }),
          ...(payload?.color2 ? { color2: payload.color2 } : { color2: "#9FC130" }),
          ...(payload?.logo_url ? { logo: payload.logo_url } : { logo: "https://cdn.leanentries.com/Entries_logo_white.png" }),
        };
      })
      .addCase(iterateModule, (state, action) => {
        if (state.module) {
          const { selectedTriggers } = action.payload;
          const selectedStep =
            state.module.chapters[action.payload.selectedStep.chapterPos]
              .questions[action.payload.selectedStep.questionPos].question_id;

          // @ts-ignore
          iterateModuleAndReturnSteps(selectedTriggers, selectedStep, state);

          if (state.moduleState) {
            state.moduleState.activeIndex =
              state.moduleState.initialization.selectedSteps.findIndex(
                ({ iterated }) => !iterated
              );
          }
        }
      })
      .addCase(emptyModule, (state) => {
        state.module = null;
        state.moduleState = null;
        state.toBePrinted = [];
        state.userProgresses = undefined;
      })
      .addCase(addTerm, (state, { payload }) => {
        if (state.terms.find((t) => t.id === payload.id) === undefined) {
          state.terms = [...state.terms, payload];
        }
      })
      .addCase(removeTerm, (state, { payload }) => {
        if (payload === "all") {
          state.terms = [];
        }
      })
      .addCase(setCurrentChapter, (state, { payload }) => {
        if (state.module && state.moduleState) {
          state.moduleState.initialization.currentChapter = payload;
        }
      })
      .addCase(loadModuleByIds.pending, (state) => {
        state.userProgresses = undefined;
        state.toBePrinted = [];
      })
      .addCase(loadModuleByIds.fulfilled, (state, action) => {
        if ("error" in action.payload) {
          state.loading = false;
          state.error = true;
        } else {
          const { module, moduleId } = action.payload;
          state.questionsWithManualActive = [];
          state.module = module;
          const intializedSteps = initializeSteps(module);
          state.moduleState = { ...intializedSteps, activeIndex: 0 };
          state.loading = false;
          state.error = false;
          state.toBePrinted = [];
        }
      })
      .addCase(fetchUserProgresses.fulfilled, (state, action) => {
        if (action.payload.response) {
          state.userProgresses = action.payload.response;
        }
      })
      .addCase(removeProgress.fulfilled, (state, action) => {
        if (action.payload.response) {
          const { progressId } = action.payload;
          state.userProgresses = state.userProgresses?.filter(
            (p) => p.user_progress_id !== progressId
          );
        }
      })
      .addCase(setUserProgress.fulfilled, (state, { payload }) => {
        if (payload.response) {
          state.savingProgress = "saved";
          if (state.userProgresses) {
            state.userProgresses = [...state.userProgresses, payload.response];
          }
        }
      })
      .addCase(activateManualForQuestion, (state, { payload }) => {
        if (!state.questionsWithManualActive) {
          state.questionsWithManualActive = [payload];
        }
        const hasQuestion = state.questionsWithManualActive.find(
          (q) => q === payload
        );
        if (!hasQuestion) {
          state.questionsWithManualActive.push(payload);
        }
      })
      .addCase(deactivateManualForQuestion, (state, { payload }) => {
        if (state.questionsWithManualActive) {
          state.questionsWithManualActive =
            state.questionsWithManualActive.filter((q) => q !== payload);
        }
      })
      .addCase(setHalfPage, (state, { payload }) => {
        state.isHalfPage = payload;
      })
      .addCase(setActiveIndex, (state, { payload }) => {
        if (state.moduleState) {
          state.moduleState.activeIndex = payload;
        }
      })
      .addCase(validateNobocapToken.fulfilled, (state, { payload }) => {
        if (payload.error) {
          state.error = true;
          return;
        }
        const firstName = payload.data.user.first_name;
        const lastName = payload.data.user.last_name;
        const email = payload.data.user.email;
        state.nobocapDetails = { firstName, lastName, email }
      })
      .addCase(validateNobocapToken.rejected, (state) => {
        state.error = true;
      })
  },
});

export const removeProgress = createAsyncThunk(
  "app/removeProgress",
  async ({ progressId }: { progressId: string }) => {
    try {
      const response = await deleteProgress({ progressId });

      return {
        response,
        progressId: progressId,
      };
    } catch (error) {
      return { error: true };
    }
  }
);

export const loadModuleByIds = createAsyncThunk(
  "app/fetchModule",
  async ({
    organizationId,
    moduleId,
  }: {
    organizationId: string;
    moduleId: string;
  }) => {
    try {
      const response = await getModule({
        organizationId,
        moduleId,
        isUpdated: true,
      });

      return {
        module: response,
        organizationId: organizationId,
        moduleId: moduleId,
      };
    } catch (error) {
      return { error: true };
    }
  }
);

export const setUserProgress = createAsyncThunk(
  "app/setUserProgress",
  async ({
    moduleId,
    userId,
    name,
    data,
    toBePrinted,
  }: {
    moduleId: string;
    userId: string;
    name: string;
    data: Record<string, any>;
    toBePrinted: any[];
  }) => {
    try {
      const response = await postUserProgress({
        userId,
        moduleId,
        name,
        data: { ...data, toBePrinted },
      });

      return {
        response,
      };
    } catch (error) {
      return { error: true };
    }
  }
);

export const fetchUserProgresses = createAsyncThunk(
  "app/getUserProgress",
  async ({ moduleId, userId }: { moduleId: string; userId: string }) => {
    try {
      const response = await getUserProgresses(userId, moduleId);

      return {
        response,
      };
    } catch (error) {
      return { error: true };
    }
  }
);

export const sendErrorNotification = createAsyncThunk("app/error_notification", async ({ receiver_email_address, custom }: { receiver_email_address: string; custom: { module_name: string; error_description: string; error_code: string; } }) => {
  try {
    const response = post("/user/error_notification", { receiver_email_address, custom });
    return {
      response,
    };
  } catch (error) {
    return { error: true };
  }
});

export const validateNobocapToken = createAsyncThunk("app/validateNobocapToken",
  async ({ token }: { token: string; }, thunkApi) => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
      const response = getWithHeaders(`${process.env.REACT_APP_NOBOCAP_BASE_URL}/validate-etool-token`, undefined, headers, false);
      return (await response).json();
    } catch (error) {
      return { error: true }
    }
  }
);


export const {
  setTableOfContent,
  setRefs,
  setLoading,
  setLoadingFalse,
  setProgress,
  setReset,
  setSavingProgress,
  setAuthType,
} = appSlice.actions;

export default appSlice.reducer;

export const selectAppState = (state: RootState) => state.app;

export const selectAuthType = createSelector(selectAppState, (app) => app.authType);

export const selectIsMSAuthenticationUsed = createSelector(
  selectAuthType,
  (authType) => authType === "ms"
);

export const selectIsAuth0AuthenticationUsed = createSelector(
  selectAuthType,
  (authType) => authType === "auth0"
);

export const selectIsNobocapAuthenticationUsed = createSelector(
  selectAuthType,
  (authType) => authType === "nobocap"
);


export const selectIsHalfPage = createSelector(
  selectAppState,
  ({ isHalfPage }) => isHalfPage
);

export const selectStyle = createSelector(selectAppState, ({ style }) => style);

export const selectAreRefsVisible = createSelector(
  selectAppState,
  ({ refs }) => refs === true
);

export const selectModule = createSelector(
  selectAppState,
  ({ module }) => module
);

export const selectModuleState = createSelector(
  selectAppState,
  ({ moduleState }) => moduleState
);

export const selectHasModuleState = createSelector(
  selectModuleState,
  (moduleState) => !!moduleState
);

export const selectTableOfContent = createSelector(
  selectAppState,
  ({ tableOfContent }) => tableOfContent
);

export const selectSavingProgress = createSelector(
  selectAppState,
  ({ savingProgress }) => savingProgress
);

export const selectUserProgresses = createSelector(
  selectAppState,
  ({ userProgresses }) => userProgresses
);

export const selectTerms = createSelector(selectAppState, ({ terms }) => terms);

export const selectToBePrinted = createSelector(
  selectAppState,
  ({ toBePrinted }) => toBePrinted
);

export const selectError = createSelector(selectAppState, ({ error }) => error);

export const selectLoading = createSelector(
  selectAppState,
  ({ loading }) => loading
);

export const selectToResetCallback = createSelector(
  selectAppState,
  ({ toResetCallback }) => toResetCallback
);

export const selectManualActiveByQuestionId = createSelector(
  selectAppState,
  (_: RootState, questionId: string) => questionId,
  ({ questionsWithManualActive }, questionId) =>
    questionsWithManualActive && questionsWithManualActive.includes(questionId)
);

export const selectSelectedSteps = createSelector(
  selectModuleState,
  (moduleState) => moduleState?.initialization.selectedSteps || []
);

export const selectActiveIndex = createSelector(
  selectModuleState,
  (moduleState) => moduleState?.activeIndex || 0
);

export const selectNobocapDetails = createSelector(
  selectAppState,
  (appState) => appState.nobocapDetails || {}
)
